import { Divider, Flex, Modal, ModalProps, ScrollArea, Stack } from '@mantine/core';
import React, { ReactNode } from 'react';

type ScrollOptions = 'all' | 'body' | 'none';

type props = ModalProps & {
  opened: boolean;
  onClose: () => void;
  title?: ModalProps['title'];
  titleFontWeight?: number;
  scrollType?: ScrollOptions;
  children: ReactNode;
  bodyPadding?: boolean;
};

const bodyPaddingProps = {
  pl: 'xl',
  pr: 'xl',
  pb: 'xl',
  pt: 'lg',
  gap: 'xs',
};

const StyledModal = ({
  opened,
  onClose,
  title,
  titleFontWeight,
  children,
  scrollType = 'all',
  styles,
  bodyPadding = true,
  onClick,
  onMouseDown,
  onPointerDown,
  ...modalProps
}: props) => {
  const customScroll = scrollType === 'body' || scrollType === 'none';
  const addScrollArea = scrollType === 'body';
  const styleOverrides = typeof styles === 'function' ? undefined : styles;

  const bodyProps = bodyPadding ? bodyPaddingProps : {};
  const body = !addScrollArea ? (
    <Stack
      {...bodyProps}
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
    >
      {children}
    </Stack>
  ) : (
    <Flex style={{ display: 'flex', flexDirection: 'column' }}>
      <ScrollArea.Autosize
        w="100%"
        mah="calc(90vh - 60px)"
        type="auto"
        // offsetScrollbars
        scrollbars={'y'}
        styles={{ content: { maxWidth: '100%' } }}
      >
        <Stack {...bodyProps}>{children}</Stack>
      </ScrollArea.Autosize>
    </Flex>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      centered
      onPointerDown={(event) => {
        onPointerDown?.(event);
        event.stopPropagation();
      }}
      onMouseDown={(event) => {
        onMouseDown?.(event);
        event.stopPropagation();
      }}
      onClick={(event) => {
        onClick?.(event);
        event.stopPropagation();
      }}
      styles={{
        title: {
          color: '#0F172A',
          fontSize: '14px',
          fontWeight: titleFontWeight ?? 500,
        },
        content: {
          borderRadius: '8px',
          border: '1px solid #ECECED',
          width: '560px',
          ...(customScroll ? { display: 'flex', flexDirection: 'column', overflow: 'hidden' } : {}),
          ...(styleOverrides?.content || {}),
        },
        header: {
          marginLeft: '15px',
          marginRight: '15px',
        },
        body: {
          padding: '0px',
          ...(customScroll ? { display: 'flex', flexDirection: 'column', minHeight: 0 } : {}),
          ...(styleOverrides?.body || {}),
        },
        close: {
          color: '#757575',
        },
        overlay: {
          backdropFilter: 'blur(2.5px)',
          background: 'rgb(186 186 186 / 0.6)',
          ...(styleOverrides?.overlay || {}),
        },
        ...styleOverrides,
      }}
      withCloseButton={false}
      {...modalProps}
    >
      {title !== undefined && <Divider style={{ borderColor: '#ECECED' }} />}
      {body}
    </Modal>
  );
};

export default StyledModal;
